import type { QuickInsertHandler } from '@atlaskit/editor-common/types';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { Snippet } from '@atlassian/editor-snippets-provider/types';

import { isAdfNodeCompatibleWithSchema } from '../../pm-plugins/schemaCompatibility';
import type { SnippetsPlugin, SnippetsPluginOptions } from '../../snippetsPluginType';
import { selectEligibleSnippets } from '../../utils/selectEligibleSnippets';
import { getSnippetQuickInsertComponents } from './getSnippetQuickInsertComponents';
import { getTemplateGalleryQuickInsertItem } from './getTemplateGalleryQuickInsertItem';
import { mapToQuickInsertItem } from './mapToQuickInsertItem';

type RegisterConfluenceSnippetsParams = {
	api: Parameters<SnippetsPlugin>[0]['api'];
	openTemplateGallery: SnippetsPluginOptions['openTemplateGallery'];
	snippets: Snippet[];
	tr: Transaction;
};

export const registerConfluenceSnippets = ({
	api,
	openTemplateGallery,
	snippets,
	tr,
}: RegisterConfluenceSnippetsParams): Transaction | null => {
	const removedSnippetIds = api?.snippets?.sharedState.currentState()?.removedSnippetIds;
	const compatibleSnippets = snippets.filter((snippet) => {
		if (removedSnippetIds?.has(snippet.snippetId)) {
			return false;
		}

		try {
			if (!isAdfNodeCompatibleWithSchema(snippet.body, tr.doc.type.schema)) {
				return false;
			}

			const snippetNode = tr.doc.type.schema.nodeFromJSON(snippet.body);
			if (snippetNode.childCount > 0) {
				snippetNode.check();
			}
			return true;
		} catch {
			return false;
		}
	});
	const eligibleSnippets = selectEligibleSnippets(compatibleSnippets);
	const isSlashCommandEnabled = isExperimentEnabled('platform_editor_slash_command');

	if (!isSlashCommandEnabled) {
		if (!api?.quickInsert) {
			return null;
		}

		const quickInsertItems: QuickInsertHandler = (intl) => [
			...(openTemplateGallery
				? [getTemplateGalleryQuickInsertItem(openTemplateGallery, intl)]
				: []),
			...eligibleSnippets.map((snippet) => mapToQuickInsertItem(snippet, api, intl)),
		];
		return api.quickInsert.commands.addQuickInsertItem(quickInsertItems)({ tr });
	}

	const snippetQuickInsertComponents = getSnippetQuickInsertComponents({
		api,
		getItem: mapToQuickInsertItem,
		snippets: eligibleSnippets,
	});
	const orderedSnippetQuickInsertComponents = openTemplateGallery
		? snippetQuickInsertComponents.map((component) => ({
				...component,
				parents: component.parents?.map((parent) => ({
					...parent,
					rank: parent.rank + 1,
				})),
			}))
		: snippetQuickInsertComponents;

	api?.uiControlRegistry?.actions.register(orderedSnippetQuickInsertComponents);
	return tr;
};
